import { initializeApp, type FirebaseApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import {
  getFirestore,
  persistentLocalCache,
  connectFirestoreEmulator,
  initializeFirestore,
  Firestore,
  persistentMultipleTabManager,
} from 'firebase/firestore'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'
import { getDatabase, connectDatabaseEmulator, type Database } from 'firebase/database'

// Firebase configuration interface
interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
}

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig)

initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})

// Initialize services
const db: Firestore = getFirestore(app)
const auth: Auth = getAuth(app)
const functions: Functions = getFunctions(app)
const storage: FirebaseStorage = getStorage(app)
const realtimeDb: Database = getDatabase(app)

const isTest = import.meta.env.MODE === 'test'
const isProd = import.meta.env.MODE === 'prod'
const isDevelopment =
  isTest || (!isProd && typeof window !== 'undefined' && window.location.hostname === 'localhost')

// Connect to emulators in development
if (isDevelopment) {
  connectFirestoreEmulator(db, 'localhost', 8080)
  connectAuthEmulator(auth, 'http://localhost:9099')
  connectFunctionsEmulator(functions, 'localhost', 5001)
  connectStorageEmulator(storage, 'localhost', 9199)
  connectDatabaseEmulator(realtimeDb, 'localhost', 9000)

  console.log('Using Firebase emulators')
} else {
  console.log('Using Firebase production services')
}

export { app, db, auth, functions, storage, realtimeDb }

// Initialize App Check (optional in dev if key missing)
try {
  const siteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY as string | undefined
  const isTest = import.meta.env.MODE === 'test'
  const isProd = import.meta.env.MODE === 'prod'
  const isDevelopment =
    isTest || (!isProd && typeof window !== 'undefined' && window.location.hostname === 'localhost')

  if (typeof window !== 'undefined' && siteKey) {
    if (isDevelopment) {
      ;(self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    })
  }
} catch (e) {
  console.warn('App Check initialization skipped:', e)
}
